import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import API from '../../../utils/API';
import Table from '../../../components/Table/Table';
import { connect } from 'react-redux';
import constants from '../../../utils/constants';
import tableCheckbox from '../../../components/Inputs/tableCheckbox';
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import useWebSocket from '../../../components/common/useWebSocket';

function RaptorLogs(props) {
  const navigate = useNavigate();
  const websocket = useWebSocket();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  const [alerts, setAlerts] = useState([]);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('startTime');
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  useEffect(() => {
    if (!permissionIds.includes(constants.permissionIds.aPlusEdit)) {
      navigate('/forbidden');
    }
  }, [navigate, permissionIds]);

  // WebSocket event listener
  useEffect(() => {
    if (!websocket) return;

    const handleWebSocketMessage = (message) => {
      const data = JSON.parse(message.data);
      if (data.messageId === 5001) {
        loadTable(dataMap, false);
      }
    };

    websocket.addEventListener('message', handleWebSocketMessage);

    return () => {
      websocket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [websocket]);

  const loadTable = async (map, resetSelection = true) => {
    // Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }
  
    try {
      const response = await API.getSinglewireAlerts();
      
      // Group alerts by externalAlertId
      const groupedAlerts = {};
      const nonGroupedAlerts = {};
      
      response.data.forEach((alert) => {
        console.log('alert', alert);
        if (alert.externalAlertId) {
          // Group alerts with externalAlertId
          if (!groupedAlerts[alert.externalAlertId]) {
            // Use the first alert as the main entry
            const startTimeDisplay = alert.createdAt ? new Date(alert.createdAt).toLocaleString() : '';
            const endTimeDisplay = alert.endedAt ? new Date(alert.endedAt).toLocaleString() : '';
            console.log('groupedAlerts', groupedAlerts);
            groupedAlerts[alert.externalAlertId] = {
              id: alert.externalAlertId,
              alertName: alert.isDrill ? `${alert.templateName} - Drill` : alert.templateName,
              siteName: alert.siteName,
              buildingId: alert.buildingId,
              singlewireSiteName: alert.singlewireSiteName,
              startTime: alert.createdAt,
              startTimeDisplay,
              endTime: alert.endedAt,
              endTimeDisplay,
              triggeredBy: alert.triggeredByName,
              clearedBy: alert.endedByName,
              isDrill: alert.isDrill,
              externalAlertId: alert.externalAlertId,
              checked: false
            };
          }
        } else {
          // Keep non-grouped alerts using their own ID
          const startTimeDisplay = alert.createdAt ? new Date(alert.createdAt).toLocaleString() : '';
          const endTimeDisplay = alert.endedAt ? new Date(alert.endedAt).toLocaleString() : '';
          
          console.log('nonGroupedAlerts', nonGroupedAlerts);
          nonGroupedAlerts[alert.id] = {
            id: alert.id,
            alertName: alert.isDrill ? `${alert.templateName} - Drill` : alert.templateName,
            siteName: alert.siteName,
            buildingId: alert.buildingId,
            singlewireSiteName: alert.singlewireSiteName,
            startTime: alert.createdAt,
            startTimeDisplay,
            endTime: alert.endedAt,
            endTimeDisplay,
            triggeredBy: alert.triggeredByName,
            clearedBy: alert.endedByName,
            isDrill: alert.isDrill,
            externalAlertId: null,
            checked: false
          };
        }
      });
      
      // Combine grouped and non-grouped alerts
      map.data = { ...groupedAlerts, ...nonGroupedAlerts };
      map.ids = Object.keys(map.data);
      map.filteredIds = [...map.ids];
  
      // Update state
      setData(Object.values(map.data));
      setDataCount(map.ids.length);
      setSelectedIds(map.selectedIds);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);
  
      // Convert map data to an array for use in your table
      const alerts = Object.values(map.data);
      setAlerts(alerts); 
    } catch (error) {
      setError('Error fetching alerts');
      console.error('Error fetching alerts:', error);
      setDataLoaded(true);
    }
  };  

  const dataMap = useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [], sortedIds: [] };
    loadTable(map);
    return map;
  }, []);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, alerts]);

  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: 'checkbox',
      accessor: 'checked',
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: 'ALERT_NAME',
      accessor: 'alertName',
      id: 'alertName',
      isVisible: true,
    },
    {
      Header: 'SINGLEWIRE_SITE_NAME',
      accessor: 'singlewireSiteName',
      id: 'singlewireSiteName',
      isVisible: true,
    },
    {
      Header: 'START_TIME',
      id: 'startTime',
      accessor: 'startTimeDisplay',
      isVisible: true,
    },
    {
      Header: 'END_TIME',
      id: 'endTime',
      accessor: 'endTimeDisplay',
      isVisible: true,
    },
    {
      Header: 'TRIGGERED_BY',
      accessor: 'triggeredBy',
      id: 'triggeredBy',
      isVisible: true,
    },
    {
      Header: 'CLEARED_BY',
      accessor: 'clearedBy',
      id: 'clearedBy',
      isVisible: true,
    },
  ];

  const tableColumns = useMemo(() => tableColumnArray, []);

  const handleExportCsv = () => {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    };

    const selectedAlerts = Object.values(dataMap.data).filter(alert => selectedIds[alert.id]);

    const csvData = selectedAlerts.map((alert) => ({
      'Alert Name': alert.alertName,
      'Singlewire Site Name': alert.singlewireSiteName,
      'Start Time': formatDate(alert.startTime),
      'End Time': alert.endTime ? formatDate(alert.endTime) : '',
      'Triggered By': alert.triggeredBy,
      'Cleared By': alert.clearedBy,
    }));

    const escapeCsvField = (field) => {
      if (typeof field === 'string') {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    const csv = [
      Object.keys(csvData[0]).map(escapeCsvField).join(','), // Header row
      ...csvData.map((row) => Object.values(row).map(escapeCsvField).join(',')), // Data rows
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `singlewire_alert_logs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const buttons = {
    footerButtons: (
      <>
        <button type="button" className="btn" disabled={Object.keys(selectedIds).length === 0} onClick={handleExportCsv}>
          <span><FormattedMessage id="EXPORT_AS_CSV" defaultMessage="Export as CSV" /></span>
        </button>
      </>
    ),
  }

  const tableProps = {
    pageType: constants.pageType.Log,
    modalId: null,
    columns: tableColumns,
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success, setSuccess,
    error, setError,
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue, 
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    buttons,
  };

  return (
    <div>
      <Table {...tableProps} />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(RaptorLogs);
